import React from 'react';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';

const ModernSelect = ({ 
  options, 
  value, 
  onChange, 
  placeholder = "Select...", 
  isDisabled = false, 
  isLoading = false,
  isSearchable = true,
  isCreatable = false,
  noOptionsMessage,
  name,
  id,
  className = "",
  label = ""
}) => {
  const SelectComponent = isCreatable ? CreatableSelect : Select;

  // Custom styles for react-select to match our design system
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: "var(--bg-surface)",
      borderColor: state.isFocused ? "var(--color-primary-500)" : "var(--border-main)",
      color: "var(--text-main)",
      minHeight: '44px',
      borderRadius: 'var(--radius-md)',
      boxShadow: state.isFocused ? '0 0 0 4px var(--color-primary-100)' : 'none',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      '&:hover': {
        borderColor: 'var(--color-primary-400)',
      }
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0 16px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      flexWrap: 'nowrap',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "var(--text-main)",
      gridArea: '1 / 1',
      marginLeft: '0',
      maxWidth: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "var(--text-muted)",
      gridArea: '1 / 1',
      marginLeft: '0',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }),
    input: (provided) => ({
      ...provided,
      color: "var(--text-main)",
      gridArea: '1 / 1',
      marginLeft: '-8px',
      padding: '0',
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: "var(--bg-surface)",
      border: "1px solid var(--border-main)",
      borderRadius: 'var(--radius-md)',
      boxShadow: 'var(--shadow-lg)',
      zIndex: 9999,
      overflow: 'hidden',
      animation: 'fadeIn 0.2s ease-out',
    }),
    menuList: (provided) => ({
      ...provided,
      padding: '4px',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected 
        ? "var(--color-primary-500)" 
        : state.isFocused
        ? "var(--color-primary-100)"
        : "transparent",
      color: state.isSelected 
        ? "#ffffff" 
        : "var(--text-main)",
      cursor: 'pointer',
      borderRadius: '4px',
      margin: '2px 0',
      transition: 'all 0.15s ease',
      "&:active": {
        backgroundColor: "var(--color-primary-500)",
      }
    }),
    loadingIndicator: (provided) => ({
      ...provided,
      color: "var(--color-primary-500)",
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: state.isFocused ? "var(--color-primary-500)" : "var(--text-muted)",
      '&:hover': {
        color: 'var(--color-primary-500)',
      }
    }),
    indicatorSeparator: () => ({
      display: 'none',
    })
  };

  // If options are simple strings/numbers, convert to {value, label} format
  const formattedOptions = options?.map(opt => 
    typeof opt === 'object' ? opt : { value: opt, label: opt }
  ) || [];

  // Find the selected option object (accepts either the value ID or the full object)
  const actualValue = value && typeof value === 'object' ? value.value : value;
  let selectedOption = formattedOptions.find(opt => opt.value === actualValue) || null;

  // Handle creatable values that aren't in the options list yet
  if (!selectedOption && value && isCreatable) {
    selectedOption = typeof value === 'object' ? value : { value: value, label: value };
  }

  return (
    <div className={`modern-select-container ${className}`}>
      {label && <label className="form-label mb-2 block font-semibold text-sm">{label}</label>}
      <SelectComponent
        id={id || name}
        name={name}
        value={selectedOption}
        onChange={(val) => onChange && onChange({ target: { name, value: val?.value ?? val } })}
        options={formattedOptions}
        isLoading={isLoading}
        isDisabled={isDisabled}
        isSearchable={isSearchable}
        placeholder={placeholder}
        styles={customStyles}
        classNamePrefix="modern-select"
        noOptionsMessage={noOptionsMessage}
      />
    </div>
  );
};


export default ModernSelect;
